import { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Icon } from "@iconify/react";
import PdfViewer from "../../Recipe/Components/PdfViewer";
import "./SendStudyOrderView.scss";
import {
  Modal,
  InputPhone,
  InputTextCustom,
  initialStatePhoneNumber,
  mobilePhoneToString,
  validMobileForInputPhone,
  PhoneNumber,
} from "@medere/medere-ui-framework";
import { generatePdf, getStudyOrderPDF, sendStudyOrder } from "../../../services/StudyOrderController";
import { EMessageType, ETaskMessageType } from "../../Recipe/RecipeEnums";
import { savePatient, hasSenderPermissionByTaskMessageTypeAndMessageType } from "../../../services/digitalRPservice";
import { validateInternationalCellphone } from "../../../utils/utils";
import { customerServiceNumber, whatsappPurchaseText } from "../../../utils/Constants";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function SendStudyOrderView({
  patient,
  studyOrderData,
  resetFlow,
  professionalId,
  onPatientUpdated,
  showPdf = true,
  initialPdfList,
  initialPdfFileId,
  onRepeat,
  repeatDisabled = false,
  cancelLabel = "Finalizar",
  onCancel,
  hideCancelConfirmation = false,
  actionHeading = "Enviar al paciente",
  helperTitle,
  helperDescription,
  expiredMode = false,
  allowPrimaryEditOnMissingContact = false,
}: any) {
  const [pdfList, setPdfList] = useState<string[]>(initialPdfList ?? []);
  const [pdfFileId, setPdfFileId] = useState<number | null>(initialPdfFileId ?? null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [sending, setSending] = useState(false);

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [sendedModalVisible, setSendedModalVisible] = useState(false);
  const [viaSended, setViaSended] = useState<number | null>(null);

  const [openEditMobileModal, setOpenEditMobileModal] = useState(false);
  const [openEditEmailModal, setOpenEditEmailModal] = useState(false);
  const [savingMobile, setSavingMobile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const [patientPhone, setPatientPhone] = useState(initialStatePhoneNumber);
  const [newMobile, setNewMobile] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [mobileErrorMsg, setMobileErrorMsg] = useState<string | null>(null);
  const [disabledWhatsapp, setDisabledWhatsapp] = useState(true);
  const [disabledEmail, setDisabledEmail] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [usedefaultcontact, setUseDefaultContact] = useState<boolean>(false);
  const [defaultEmail, setUseDefaultEmail] = useState<string>(patient?.email ?? "");
  const [defaultMobilePhone, setUseDefaultMobilePhone] = useState<string>(patient?.mobilephone ?? "");

  const studyOrderId = studyOrderData?.studyOrderId;
  const phoneToUse = usedefaultcontact ? defaultMobilePhone : (patient?.mobilephone ?? "");
  const emailToUse = usedefaultcontact ? defaultEmail : (patient?.email ?? "");
  const hasPhone = !!phoneToUse?.trim();
  const hasEmail = !!emailToUse?.trim();
  const canSendWhatsapp = !disabledWhatsapp && hasPhone && !sending;
  const canSendEmail = !disabledEmail && hasEmail && !sending;
  const canEditWhatsapp = !usedefaultcontact;
  const canEditEmail = !disabledEmail && !usedefaultcontact;
  const canSend = !!pdfFileId && !!professionalId && !sending;

  const pdfFitStyle: React.CSSProperties = {
    display: "inline-block",
    width: "fit-content",
    margin: "0 auto",
  };

  useEffect(() => {
    const fetchPermission = async () => {
      try {
        const [waDto, emailDto] = await Promise.all([
          hasSenderPermissionByTaskMessageTypeAndMessageType(
            EMessageType.WHATSAPP,
            ETaskMessageType.STUDY_ORDERS
          ),
          hasSenderPermissionByTaskMessageTypeAndMessageType(
            EMessageType.EMAIL,
            ETaskMessageType.STUDY_ORDERS
          ),
        ]);
        setDisabledWhatsapp(!waDto?.hassenderpermission);
        setDisabledEmail(!emailDto?.hassenderpermission);
        setUseDefaultContact(Boolean(waDto?.usedefaultcontact || emailDto?.usedefaultcontact));
        setUseDefaultEmail(
          String(waDto?.defaultemail ?? emailDto?.defaultemail ?? patient?.email ?? "")
        );
        setUseDefaultMobilePhone(
          String(waDto?.defaultmobilephone ?? emailDto?.defaultmobilephone ?? patient?.mobilephone ?? "")
        );
      } catch {
        setDisabledWhatsapp(true);
        setDisabledEmail(true);
      } finally {
        setLoadingPerms(false);
      }
    };

    fetchPermission();
  }, [patient?.email, patient?.mobilephone]);

  useEffect(() => {
    if (initialPdfList?.length) setPdfList(initialPdfList);
    if (initialPdfFileId) setPdfFileId(initialPdfFileId);
  }, [initialPdfFileId, initialPdfList]);

  useEffect(() => {
    if (!studyOrderId) return;
    if (initialPdfList?.length && initialPdfFileId) return;

    setLoadingPdf(true);

    generatePdf(studyOrderId)
      .then(() => getStudyOrderPDF(studyOrderId))
      .then((file) => {
        if (file?.encodedPdf) {
          setPdfList([file.encodedPdf]);
          setPdfFileId(file.id);
        }
      })
      .catch(() => setErrorMsg("No se pudo generar/cargar el PDF de la orden."))
      .finally(() => setLoadingPdf(false));
  }, [initialPdfFileId, initialPdfList, studyOrderId]);

  const doSend = async (type: number) => {
    if (!canSend) return;
    if (type === EMessageType.WHATSAPP && disabledWhatsapp) {
      setErrorMsg("La institución no tiene contratado el envío por WhatsApp.");
      return;
    }
    if (type === EMessageType.EMAIL && disabledEmail) {
      setErrorMsg("La institución no tiene habilitado el envío por Email.");
      return;
    }
    if (type === EMessageType.WHATSAPP && !hasPhone) {
      setErrorMsg("No hay un celular cargado para enviar por WhatsApp.");
      return;
    }
    if (type === EMessageType.EMAIL && !hasEmail) {
      setErrorMsg("No hay un mail cargado para enviar por Email.");
      return;
    }

    if (type === EMessageType.WHATSAPP) {
      const validation = validateInternationalCellphone(phoneToUse);
      if (!validation.isValid()) {
        setErrorMsg("El número de celular no es válido. Revisá el número.");
        return;
      }
    }

    try {
      setSending(true);
      await sendStudyOrder({
        fileId: pdfFileId!,
        messageType: type,
        patientId: patient.medereentity,
        professionalId,
      });
      setViaSended(type);
      setSendedModalVisible(true);
      setErrorMsg(null);
    } catch {
      setErrorMsg("No se pudo enviar la orden en este momento. Intente nuevamente.");
    } finally {
      setSending(false);
    }
  };

  const handleDownload = () => {
    if (!pdfList[0]) return;
    const a = document.createElement("a");
    a.href = `data:application/pdf;base64,${pdfList[0]}`;
    a.download = "orden_estudios.pdf";
    a.click();
  };

  const handleShare = async () => {
    if (!navigator.share || !pdfList[0]) return;

    const bytes = Uint8Array.from(atob(pdfList[0]), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });

    try {
      await navigator.share({
        title: "Orden de estudios",
        files: [new File([blob], "orden_estudios.pdf", { type: "application/pdf" })],
      });
    } catch {
      setErrorMsg("No se pudo compartir el archivo desde este dispositivo.");
    }
  };

  const completeFlow = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    resetFlow();
  };

  const handleCancel = () => {
    if (hideCancelConfirmation) {
      completeFlow();
      return;
    }
    setCancelModalVisible(true);
  };

  const handleOpenEditMobileModal = () => {
    setErrorMsg(null);
    setMobileErrorMsg(null);
    if (patient?.mobilephone) {
      setPatientPhone(validMobileForInputPhone(patient.mobilephone));
      setNewMobile(patient.mobilephone);
    } else {
      setPatientPhone(initialStatePhoneNumber);
      setNewMobile("");
    }
    setOpenEditMobileModal(true);
  };

  const handleOpenEditEmailModal = () => {
    setErrorMsg(null);
    setNewEmail(patient?.email ?? "");
    setOpenEditEmailModal(true);
  };

  const handleWhatsappPrimaryAction = () => {
    if (allowPrimaryEditOnMissingContact && !hasPhone) {
      handleOpenEditMobileModal();
      return;
    }

    doSend(EMessageType.WHATSAPP);
  };

  const handleEmailPrimaryAction = () => {
    if (allowPrimaryEditOnMissingContact && !hasEmail) {
      handleOpenEditEmailModal();
      return;
    }

    doSend(EMessageType.EMAIL);
  };

  return (
    <div className={`send-study-order-view${expiredMode ? " expired-mode" : ""}`}>
      <div className="ssov-body">
        {showPdf && (
          <div className="ssov-pdf">
            {loadingPdf && <span>Cargando PDF...</span>}
            {!loadingPdf && pdfList.length > 0 && (
              <div style={pdfFitStyle}>
                <PdfViewer fileList={pdfList} />
              </div>
            )}
          </div>
        )}

        <div className="ssov-actions">
          <div className="ssov-actions-main">
            <h3 className="ssov-heading">{actionHeading}</h3>
            {helperTitle && (
              <div className={`ssov-helper${expiredMode ? " ssov-helper-expired" : ""}`}>
                <strong>{helperTitle}</strong>
                {helperDescription && <span>{helperDescription}</span>}
              </div>
            )}
            {errorMsg && <small className="ssov-warning"><span>{errorMsg}</span></small>}

            {!expiredMode && (
              <>
                <div className="ssov-row">
                  <Button
                    icon={<span className="p-button-icon p-c"><Icon icon="bi:chat" /></span>}
                    label={`Enviar por whatsapp a ${phoneToUse}`}
                    onClick={handleWhatsappPrimaryAction}
                    disabled={!canSendWhatsapp}
                    className="ssov-btn-main ssov-btn-recipe-like ssov-btn-whatsapp"
                  />

                  <Button
                    icon={<span className="p-button-icon p-c"><Icon icon={hasPhone ? "bi:pencil-square" : "bi:plus"} /></span>}
                    onClick={handleOpenEditMobileModal}
                    className="btnSOBack ssov-btn-icon-edit"
                  />
                </div>

                {!loadingPerms && disabledWhatsapp && (
                  <small className="ssov-warning">
                    <i className="pi pi-exclamation-triangle" />
                    <span>Servicio con costo adicional.</span>
                    <a
                      href={`https://wa.me/${customerServiceNumber}?text=${whatsappPurchaseText}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Contratar ahora
                    </a>
                  </small>
                )}

                <div className="ssov-row">
                  <Button
                    icon={<span className="p-button-icon p-c"><Icon icon="bi:envelope" /></span>}
                    label={`Enviar a ${emailToUse}`}
                    onClick={handleEmailPrimaryAction}
                    disabled={!canSendEmail}
                    className="ssov-btn-main ssov-btn-recipe-like ssov-btn-email"
                  />
                  <Button
                    icon={<span className="p-button-icon p-c"><Icon icon={hasEmail ? "bi:pencil-square" : "bi:plus"} /></span>}
                    onClick={handleOpenEditEmailModal}
                    className="btnSOBack ssov-btn-icon-edit"
                    disabled={!canEditEmail}
                  />
                </div>

                <Button
                  icon={<span className="p-button-icon p-c"><Icon icon="bi:send" /></span>}
                  label="Compartir por otros medios"
                  onClick={handleShare}
                  className="ssov-btn-secondary ssov-btn-recipe-like"
                />

                <Button
                  icon={<span className="p-button-icon p-c"><Icon icon="bi:download" /></span>}
                  label="Descargar orden"
                  onClick={handleDownload}
                  className="ssov-btn-secondary ssov-btn-recipe-like"
                />
              </>
            )}
          </div>

          <div className="ssov-actions-footer">
            {onRepeat && (
              <Button
                label="Repetir prescripción"
                onClick={onRepeat}
                disabled={repeatDisabled}
                className="ssov-btn-secondary ssov-btn-recipe-like ssov-repeat-button"
              />
            )}

            <Button
              label={cancelLabel}
              onClick={handleCancel}
              className="ssov-btn-cancel ssov-btn-recipe-cancel"
            />
          </div>
        </div>
      </div>

      <Modal
        visible={openEditMobileModal}
        setVisible={setOpenEditMobileModal}
        header={
          <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
            <strong>
              {patient?.mobilephone
                ? "Editar celular del paciente"
                : "Ingresar celular del paciente"}
            </strong>
          </div>
        }
        headerStyle={{ fontSize: "12px", textAlign: "center" }}
        width="w-medium"
      >
        <div className="ssov-mobile-phone-field">
          <small
            className="ssov-mobile-phone-example"
            style={{
              display: "block",
              width: "fit-content",
              marginLeft: "135px",
              marginBottom: "4px",
              fontSize: "12px",
              lineHeight: "1.2",
              color: "#60708a",
            }}
          >
            Ejemplo: 2231234567
          </small>
          <InputPhone
            cboClassname="border2 w-4"
            inputClassname="border2"
            value={patientPhone}
            label=""
            floatLabel={false}
            setValue={(mobile: PhoneNumber) => {
              setMobileErrorMsg(null);
              setPatientPhone(mobile);
              setNewMobile(mobilePhoneToString(mobile));
            }}
          />

          {mobileErrorMsg && (
            <small style={{ color: "#d32f2f", marginTop: "8px", display: "block" }}>
              {mobileErrorMsg}
            </small>
          )}

          <Button
            label={patient?.mobilephone ? "Cambiar" : "Agregar"}
            onClick={() => {
              const validation = validateInternationalCellphone(newMobile);
              if (!validation.isValid()) {
                setMobileErrorMsg("Número inválido. Característica sin 0 y número sin 15.");
                return;
              }
              const normalizedMobile = newMobile.replace(/[^+\d]/g, "");
              setSavingMobile(true);
              savePatient({ ...patient, mobilephone: normalizedMobile }, 0, 0)
                .then(() => {
                  onPatientUpdated?.({ ...patient, mobilephone: normalizedMobile });
                  setMobileErrorMsg(null);
                  setOpenEditMobileModal(false);
                })
                .catch(() => setMobileErrorMsg("No se pudo actualizar el celular del paciente."))
                .finally(() => setSavingMobile(false));
            }}
            style={{
              marginTop: "8px",
              width: "100%",
              display: "block",
              backgroundColor: newMobile?.trim() !== "" ? "#14C7BC" : "#9E9E9E",
              border: "none",
            }}
            loading={savingMobile}
            disabled={newMobile?.trim() === "" || savingMobile}
          />
        </div>
      </Modal>

      <Modal
        visible={openEditEmailModal}
        setVisible={setOpenEditEmailModal}
        header={
          <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
            <strong>
              {patient?.email
                ? "Editar mail del paciente"
                : "Ingresar mail del paciente"}
            </strong>
          </div>
        }
        headerStyle={{ fontSize: "12px", textAlign: "center" }}
        width="w-medium"
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <InputTextCustom
            className="border2"
            value={newEmail}
            onChange={(e: any) => {
              setErrorMsg(null);
              setNewEmail(e.target.value);
            }}
          />

          {errorMsg && (
            <small style={{ color: "#d32f2f", marginTop: "8px", display: "block" }}>
              {errorMsg}
            </small>
          )}

          <Button
            label={patient?.email ? "Cambiar" : "Agregar"}
            onClick={() => {
              if (!isValidEmail(newEmail)) {
                setErrorMsg("El formato del mail no es válido.");
                return;
              }
              setSavingEmail(true);
              savePatient({ ...patient, email: newEmail }, 0, 0)
                .then(() => {
                  onPatientUpdated?.({ ...patient, email: newEmail });
                  setErrorMsg(null);
                  setOpenEditEmailModal(false);
                })
                .catch(() => setErrorMsg("No se pudo actualizar el email del paciente."))
                .finally(() => setSavingEmail(false));
            }}
            style={{
              marginTop: "8px",
              backgroundColor: newEmail?.trim() !== "" ? "#14C7BC" : "#9E9E9E",
              border: "none",
            }}
            loading={savingEmail}
            disabled={newEmail?.trim() === "" || savingEmail}
          />
        </div>
      </Modal>

      <Modal
        visible={cancelModalVisible}
        setVisible={setCancelModalVisible}
        className="sendorder-cancel-modal"
        header={null}
        closable={false}
      >
        <div className="sendorder-cancel-content">
          <h2 className="sendorder-cancel-title">¿Querés cancelar el envío de esta orden?</h2>

          <p className="sendorder-cancel-text">
            Tené en cuenta que tu orden ya fue generada.
            <br />
            Si cancelás su envío, podés verla en la <strong>sección historial</strong>.
          </p>

          <div className="sendorder-cancel-actions">
            <Button
              label="Volver a la vista previa"
              className="sendorder-btn-outline sendorder-cancel-button"
              onClick={() => setCancelModalVisible(false)}
            />

            <Button
              label="Sí, cancelar el envío"
              className="sendorder-btn-filled sendorder-cancel-button"
              onClick={() => {
                setCancelModalVisible(false);
                completeFlow();
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal visible={sendedModalVisible} setVisible={setSendedModalVisible} className="sendorder-sent-modal">
        <div className="ssov-success">
          <div className="ssov-success__iconWrap" aria-hidden>
            <Icon icon="bi:check-lg" width={44} height={44} />
          </div>

          <h2 className="ssov-success__title">La orden fue enviada</h2>

          <p className="ssov-success__subtitle">
            {viaSended === EMessageType.EMAIL ? (
              <>
                El paciente <strong>{patient?.fullname ?? ""}</strong> lo recibirá en:{" "}
                <strong>{patient?.email ?? ""}</strong>
              </>
            ) : (
              <>
                El paciente <strong>{patient?.fullname ?? ""}</strong> lo recibirá en:{" "}
                <strong>{patient?.mobilephone ?? ""}</strong>
              </>
            )}
          </p>

          <div className="ssov-success__actions">
            <Button
              label="Finalizar"
              className="ssov-success__btn ssov-success__btn--primary"
              onClick={() => {
                setSendedModalVisible(false);
                completeFlow();
              }}
            />

            <Button
              label="Reenviar por otro medio"
              className="ssov-success__btn ssov-success__btn--secondary"
              onClick={() => setSendedModalVisible(false)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
